import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PasswordInput } from '../components/PasswordInput';
import { api, ApiError } from '../lib/api';
import { useSession } from '../store/session';
import { Glyph } from '../components/GameIcon';

const MIN = 8;

/**
 * Where a "forgot password" email's link lands (/reset-password?token=…), signed in or not: choose a
 * new password, typed twice. Saving it signs the account out everywhere, so this device signs in again.
 */
export function ResetPassword() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [state, setState] = useState<'form' | 'done' | 'invalid'>(token.length >= 20 ? 'form' : 'invalid');
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length < MIN;
  const mismatch = confirm !== password;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (tooShort || mismatch || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/reset', { body: { token, password }, auth: false });
      // The server has signed the account out everywhere; drop this device's session too.
      if (useSession.getState().status === 'authed') await useSession.getState().logout().catch(() => {});
      setState('done');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && /expired|used/.test(err.message)) setState('invalid');
      else setError(t('errors.network'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="welcome reset-page">
      <div className="card reset-card">
        <h1>{t('reset.title')}</h1>
        {state === 'invalid' ? (
          <>
            <p className="error-text" role="alert" style={{ fontSize: 15 }}>{t('reset.invalid')}</p>
            <button className="btn primary lg block" onClick={() => nav('/', { replace: true })}>{t('reset.signIn')}</button>
          </>
        ) : state === 'done' ? (
          <>
            <div className="notice success" role="status"><Glyph e="✅" /> {t('reset.done')}</div>
            <button className="btn primary lg block" onClick={() => nav('/', { replace: true })}>{t('reset.signIn')}</button>
          </>
        ) : (
          <form className="stack" onSubmit={submit} noValidate>
            <div className="field">
              <label htmlFor="new-password">{t('reset.password')}</label>
              <PasswordInput id="new-password" autoFocus autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} maxLength={128} aria-invalid={tried && tooShort} />
              {tried && tooShort ? <span className="error-text">{t('reset.tooShort')}</span> : <span className="hint">{t('reset.tooShort')}</span>}
            </div>
            <div className="field">
              <label htmlFor="confirm-password">{t('reset.confirm')}</label>
              <PasswordInput id="confirm-password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} maxLength={128} aria-invalid={tried && mismatch} />
              {tried && mismatch && <span className="error-text">{t('reset.mismatch')}</span>}
            </div>
            {error && <div className="error-text" role="alert">{error}</div>}
            <button className="btn primary lg block" disabled={busy}>{busy ? <span className="spin" /> : t('reset.submit')}</button>
          </form>
        )}
      </div>
    </div>
  );
}
